// src/pages/DebriefTable/index.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Dropdown, Input, Select, Tag, Tooltip, message } from "antd";
import { CopyOutlined, DownOutlined, MoreOutlined } from "@ant-design/icons";
import { functionService } from "../../lib/functions";
import * as Styled from "./styled";
import eyeWhite from "../../images/eyeWhite.svg";
import Sidemenu from "../../components/sidemenu";
import DownloadAssets from "../downloadAssets/download";
import { LoadingOutlined } from "@ant-design/icons";
import {
    debounce,
    filterData,
    formatPhoneNumber,
    getStatusBadgeStyle,
    truncateText,
} from "./helpers";

const paginationStyles = {
    container: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "16px 4px",
        borderTop: "1px solid #F0F0F0",
        marginTop: "8px",
    },
    leftSection: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        fontSize: "13px",
        color: "#6B7280",
    },
    pageControls: {
        display: "flex",
        alignItems: "center",
        gap: "4px",
    },
    pageBtn: (active, disabled) => ({
        minWidth: "34px",
        height: "34px",
        padding: "0 10px",
        borderRadius: "8px",
        border: active ? "none" : "1px solid #E5E7EB",
        background: active ? "#4F46E5" : disabled ? "#F9FAFB" : "#FFFFFF",
        color: active ? "#FFFFFF" : disabled ? "#D1D5DB" : "#374151",
        fontWeight: active ? 600 : 400,
        fontSize: "13px",
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "all 0.15s ease",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: active ? "0 1px 4px rgba(79,70,229,0.3)" : "none",
    }),
    ellipsis: {
        width: "34px",
        height: "34px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#9CA3AF",
        fontSize: "14px",
        userSelect: "none",
    },
    navBtn: (disabled) => ({
        height: "34px",
        padding: "0 14px",
        borderRadius: "8px",
        border: "1px solid #E5E7EB",
        background: disabled ? "#F9FAFB" : "#FFFFFF",
        color: disabled ? "#D1D5DB" : "#374151",
        fontWeight: 500,
        fontSize: "13px",
        cursor: disabled ? "not-allowed" : "pointer",
        display: "flex",
        alignItems: "center",
        gap: "4px",
        transition: "all 0.15s ease",
    }),
};

const DebriefTable = () => {
    // Main data states
    const [debriefList, setDebriefList] = useState([]);
    const [filteredDebriefList, setFilteredDebriefList] = useState([]);

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    //for loading state
    const [downloadingRowId, setDownloadingRowId] = useState(null);

    // Filter selection states
    const [filters, setFilters] = useState({
        company: null,
        role: null,
        recruiter: null,
        jobCode: null,
        search: "",
    });

    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    /* ── Fetch debrief list ──────────────────────────────────────────────────── */
    const fetchDebriefList = async () => {
        try {
            setLoading(true);

            const response = await functionService.get("demand/debrief-list");

            if (response?.status) {
                const data = response?.response?.data?.data || [];

                const sortedData = [...data].sort(
                    (a, b) => new Date(b.created_at) - new Date(a.created_at)
                );

                setDebriefList(sortedData);
                message.success("Data loaded successfully");
            } else {
                message.error("Failed to fetch debrief data");
            }
        } catch (error) {
            console.error("Error in fetchDebriefList:", error);
            message.error("Error fetching debrief data");
        } finally {
            setLoading(false);
        }
    };

    /* ── Initial data load ───────────────────────────────────────────────────── */
    useEffect(() => {
        fetchDebriefList();
    }, []);

    /* ── Apply filters using useMemo ─────────────────────────────────────────── */
    const filtered = useMemo(() => {
        return filterData(debriefList, filters);
    }, [debriefList, filters]);

    /* ── Update filtered list and reset pagination ──────────────────────────── */
    useEffect(() => {
        setFilteredDebriefList(filtered);
        setCurrentPage(1);
    }, [filtered]);

    /* ── Debounced search handler ────────────────────────────────────────────── */
    const handleSearchChange = useCallback(
        debounce((value) => {
            setFilters((prev) => ({ ...prev, search: value }));
        }, 300),
        []
    );

    /* ── Handle filter change ────────────────────────────────────────────────── */
    const handleFilterChange = (filterKey, value) => {
        setFilters((prev) => ({
            ...prev,
            [filterKey]: value,
        }));
    };

    /* ── Pagination calculations ─────────────────────────────────────────────── */
    const totalFilteredPages = Math.ceil(filteredDebriefList.length / itemsPerPage);
    const itemsToDisplay = filteredDebriefList.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    /* ── Download file utility ───────────────────────────────────────────────── */
    const downloadFile = (url, docType, item) => {
        if (!url) {
            message.info("No file available for download");
            return;
        }
        const candidateName = `${item.first_name || ""} ${item.last_name || ""}`.trim();
        const filename = `${docType}_${item.company_name || "document"}_${candidateName || "candidate"
            }`.replace(/\s+/g, "_");
        DownloadAssets(url, filename);
    };

    const downloadAssessmentReport = async (item) => {
        if (!item.assessment_id) {
            message.warning("Assessment report not available");
            return;
        }

        if (downloadingRowId === item.id) return;

        setDownloadingRowId(item.id);

        try {
            const response = await functionService.getReportWithResume(
                `demand/download-assessment-report/${item.assessment_id}`
            );

            const blob = new Blob([response], {
                type: "application/pdf",
            });

            const url = URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = url;

            const filename =
                `${item.first_name || ""}_${item.last_name || ""}_Assessment_Report`
                    .replace(/\s+/g, "_")
                    .replace(/\./g, "");

            a.download = `${filename}.pdf`;

            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error downloading assessment report:", error);
            message.error("Failed to download assessment report");
        }
        finally {
            setDownloadingRowId(null);
        }
    };

    /* ── Copy debrief text to clipboard ─────────────────────────────────── */
    const copyDebriefToClipboard = (debriefText) => {
        if (!debriefText) {
            message.error("No debrief text to copy");
            return;
        }
        navigator.clipboard.writeText(debriefText).then(() => {
            message.success("Debrief copied successfully");
        }).catch(() => {
            message.error("Failed to copy");
        });
    };

    /* ── Get unique options for dropdowns ─────────────────────────────────────── */
    const getRoleOptions = useMemo(() => {
        const filtered = filters.company
            ? debriefList.filter((item) => item.company_name === filters.company)
            : debriefList;
        return [...new Set(filtered.map((item) => item.role_title).filter(Boolean))].sort();
    }, [debriefList, filters.company]);

    const getCompanyOptions = useMemo(() => {
        return [
            ...new Set(
                debriefList
                    .map((item) => item.company_name)
                    .filter(Boolean)
            ),
        ].sort();
    }, [debriefList]);

    const getRecruiterOptions = useMemo(() => {
        const filtered = filters.role
            ? debriefList.filter(
                (item) => item.role_title === filters.role
            )
            : debriefList;

        return [
            ...new Set(
                filtered
                    .map((item) => item.recruiter_name)
                    .filter(Boolean)
            ),
        ].sort();
    }, [debriefList, filters.role]);

    const getJobCodeOptions = useMemo(() => {
        const filtered = filters.company
            ? debriefList.filter((item) => item.company_name === filters.company)
            : debriefList;

        return [
            ...new Set(
                filtered
                    .map((item) => item.job_code)
                    .filter(Boolean)
            ),
        ].sort();
    }, [debriefList, filters.company]);

    /* ── Build pagination page numbers ───────────────────────────────────────── */
    const buildPageNumbers = () => {
        const pages = [];
        if (totalFilteredPages <= 7) {
            for (let i = 1; i <= totalFilteredPages; i++) pages.push(i);
            return pages;
        }
        pages.push(1);
        if (currentPage > 3) pages.push("...");
        for (
            let i = Math.max(2, currentPage - 1);
            i <= Math.min(totalFilteredPages - 1, currentPage + 1);
            i++
        ) {
            pages.push(i);
        }
        if (currentPage < totalFilteredPages - 2) pages.push("...");
        pages.push(totalFilteredPages);
        return pages;
    };

    /* ── Render table rows ───────────────────────────────────────────────────── */
    const renderTableRows = (items) =>
        items.map((item, index) => {
            const isDownloading = downloadingRowId === item.id;
            return (
                <tr key={`${item.id}-${index}`}>

                    {/* Candidate Details */}
                    <td className="sticky-col-1">
                        {`${item.first_name || ""} ${item.last_name || ""}`.trim() || "-"}
                        <br />
                        {item.email || "-"}
                        <br />
                        {formatPhoneNumber(item.primary_no)}
                    </td>
                    <td className="sticky-col-2">{item.company_name || "-"}</td>
                    <td className="sticky-col-3">{item.role_title || "-"}</td>
                    <td>
                        {item.job_code || "-"}
                    </td>
                    <td>{item.recruiter_name || "-"}</td>

                    <td>
                        {item.total_work_exp
                            ? `${parseFloat(item.total_work_exp).toFixed(1)} yrs`
                            : "-"}
                    </td>
                    <td>
                        {item.relevant_experience
                            ? `${parseFloat(item.relevant_experience).toFixed(1)} yrs`
                            : "-"}
                    </td>
                    <td>
                        <Tag
                            style={{
                                ...getStatusBadgeStyle(item.candidate_current_status),
                                border: `1px solid ${getStatusBadgeStyle(item.candidate_current_status).border
                                    }`,
                                fontWeight: 600,
                                fontSize: "11px",
                                padding: "4px 8px",
                            }}
                        >
                            {item.candidate_current_status || "Pending"}
                        </Tag>
                    </td>
                    <td>
                        <Tooltip title={item.client_feedback || "-"} placement="topLeft">
                            <span className="text-truncate">
                                {truncateText(item.client_feedback, 20)}
                            </span>
                        </Tooltip>
                    </td>
                    <td>
                        <Tooltip title={item.client_remark || "-"} placement="topLeft">
                            <span className="text-truncate">
                                {truncateText(item.client_remark, 20)}
                            </span>
                        </Tooltip>
                    </td>
                    <td>{item.fitment_score ? `${item.fitment_score}` : "-"}</td>
                    <td>
                        <Tag
                            color={
                                item.assessment_status === "Completed"
                                    ? "green"
                                    : item.assessment_status === "Pending"
                                        ? "orange"
                                        : item.assessment_status === "In Review"
                                            ? "blue"
                                            : "default"
                            }
                            style={{ fontWeight: 600, fontSize: "11px" }}
                        >
                            {item.assessment_status || "Not Submitted"}
                        </Tag>
                    </td>
                    <td>{item.tech_score ? `${item.tech_score}%` : "-"}</td>
                    <td>
                        {item.red_flag !== null && item.red_flag !== undefined
                            ? item.red_flag
                            : "-"}
                    </td>

                    {/* Debrief Column with Dropdown */}
                    <td>
                        {/* ✅ CHANGE 1: Define sortedDebrief once at the top */}
                        {(() => {
                            const sortedDebrief = [
                                ...(
                                    typeof item.debrief_form === "string"
                                        ? JSON.parse(item.debrief_form)
                                        : item.debrief_form || []
                                ),
                            ].sort((a, b) => {
                                const roundA = parseInt(
                                    (a.round_name || "").match(/\d+/)?.[0] || "999"
                                );
                                const roundB = parseInt(
                                    (b.round_name || "").match(/\d+/)?.[0] || "999"
                                );
                                return roundA - roundB;
                            });

                            // NEW: NO Debrief Flow - parse debrief_no_reason the
                            // same safe way debrief_form is parsed above
                            const noReasonList = [
                                ...(
                                    typeof item.debrief_no_reason === "string"
                                        ? JSON.parse(item.debrief_no_reason)
                                        : item.debrief_no_reason || []
                                ),
                            ];

                            // NEW: NO Debrief Flow - extract the numeric round
                            // number regardless of "R3" vs "Round 3" formatting,
                            // reusing the same digit-extraction approach the
                            // existing sort above already relies on
                            const getRoundNumber = (roundName) =>
                                parseInt((roundName || "").match(/\d+/)?.[0] || "999", 10);

                            // NEW: NO Debrief Flow - build a combined, round-ordered
                            // list for display only. debrief_form entries always win
                            // for a given round; a NO entry is only included when
                            // that round has no YES debrief. debrief_form itself is
                            // never modified - this array is separate/display-only.
                            const combinedRounds = [
                                ...sortedDebrief.map((round) => ({
                                    type: "yes",
                                    roundNumber: getRoundNumber(round.round_name),
                                    data: round,
                                })),
                                ...noReasonList
                                    .filter(
                                        (round) =>
                                            !sortedDebrief.some(
                                                (yesRound) =>
                                                    getRoundNumber(yesRound.round_name) ===
                                                    getRoundNumber(round.round_name),
                                            ),
                                    )
                                    .map((round) => ({
                                        type: "no",
                                        roundNumber: getRoundNumber(round.round_name),
                                        data: round,
                                    })),
                            ].sort((a, b) => a.roundNumber - b.roundNumber);

                            return (
                                <>
                                    {/* CHANGED: trigger button now shows if either a YES
                                        debrief or a NO reason exists for any round */}
                                    {combinedRounds.length > 0 ? (
                                        <Dropdown
                                            menu={{
                                                items: [
                                                    {
                                                        key: "content",
                                                        label: (
                                                            <div
                                                                style={{
                                                                    maxWidth: "320px",
                                                                    padding: "10px",
                                                                    position: "relative",
                                                                }}
                                                            >
                                                                {/* Copy Icon - EXISTING: still copies only the YES/debrief_form rounds, unchanged */}
                                                                <Button
                                                                    type="text"
                                                                    size="small"
                                                                    icon={<CopyOutlined />}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();

                                                                        {/* ✅ CHANGE 3a: Delete the old sortedDebrief definition and use the one from above */ }
                                                                        const debriefText = sortedDebrief
                                                                            .map(
                                                                                (round) =>
                                                                                    `${round.round_name}\n${round.questions?.join("\n")}\n${round.additional_information || ""
                                                                                    }`
                                                                            )
                                                                            .join("\n\n");

                                                                        copyDebriefToClipboard(debriefText);
                                                                    }}
                                                                    style={{
                                                                        position: "absolute",
                                                                        top: "6px",
                                                                        right: "6px",
                                                                        padding: 0,
                                                                        width: "24px",
                                                                        height: "24px",
                                                                        minWidth: "24px",
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                        color: "#1b49ea",
                                                                    }}
                                                                />

                                                                {/* Heading */}
                                                                <div
                                                                    style={{
                                                                        fontSize: "12px",
                                                                        fontWeight: 600,
                                                                        color: "#111827",
                                                                        marginBottom: "8px",
                                                                        paddingRight: "30px",
                                                                    }}
                                                                >
                                                                    Debrief Details
                                                                </div>

                                                                {/* Debrief Content */}
                                                                <div
                                                                    style={{
                                                                        maxHeight: "300px",
                                                                        overflowY: "auto",
                                                                        paddingRight: "4px",
                                                                    }}
                                                                >
                                                                    {/* CHANGED: iterate combinedRounds instead of sortedDebrief so
                                                                        NO rounds are interspersed in round order alongside YES rounds */}
                                                                    {combinedRounds.map((entry, index) =>
                                                                        entry.type === "yes" ? (
                                                                            // EXISTING: YES round card - completely unchanged
                                                                            <div
                                                                                key={index}
                                                                                style={{
                                                                                    marginBottom: "14px",
                                                                                    background: "#f8fafc",
                                                                                    border: "1px solid #e5e7eb",
                                                                                    borderRadius: "10px",
                                                                                    padding: "12px",
                                                                                    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                                                                                }}
                                                                            >
                                                                                {/* Round Header */}
                                                                                <div
                                                                                    style={{
                                                                                        display: "flex",
                                                                                        justifyContent: "space-between",
                                                                                        alignItems: "center",
                                                                                        marginBottom: "10px",
                                                                                        borderBottom: "1px solid #e5e7eb",
                                                                                        paddingBottom: "8px",
                                                                                    }}
                                                                                >
                                                                                    <div
                                                                                        style={{
                                                                                            fontWeight: 700,
                                                                                            fontSize: "13px",
                                                                                            color: "#1b49ea",
                                                                                        }}
                                                                                    >
                                                                                        {entry.data.round_name}
                                                                                    </div>

                                                                                    <div
                                                                                        style={{
                                                                                            fontSize: "11px",
                                                                                            color: "#6b7280",
                                                                                        }}
                                                                                    >
                                                                                        {entry.data.submitted_at
                                                                                            ? new Date(
                                                                                                entry.data.submitted_at
                                                                                            ).toLocaleString()
                                                                                            : "No Date"}
                                                                                    </div>
                                                                                </div>

                                                                                {/* Questions Heading */}
                                                                                <div
                                                                                    style={{
                                                                                        fontSize: "11px",
                                                                                        fontWeight: 600,
                                                                                        color: "#6b7280",
                                                                                        marginBottom: "8px",
                                                                                        textTransform: "uppercase",
                                                                                        letterSpacing: "0.5px",
                                                                                    }}
                                                                                >
                                                                                    Questions Asked
                                                                                </div>

                                                                                {/* Questions */}
                                                                                {entry.data.questions?.map((question, qIndex) => (
                                                                                    <div
                                                                                        key={qIndex}
                                                                                        style={{
                                                                                            padding: "8px 10px",
                                                                                            background: "#fff",
                                                                                            border: "1px solid #e5e7eb",
                                                                                            borderRadius: "6px",
                                                                                            marginBottom: "6px",
                                                                                            fontSize: "12px",
                                                                                            color: "#111827",
                                                                                            wordBreak: "break-word",
                                                                                            whiteSpace: "normal",
                                                                                            lineHeight: "1.5",
                                                                                        }}
                                                                                    >
                                                                                        <strong>{qIndex + 1}.</strong> {question}
                                                                                    </div>
                                                                                ))}

                                                                                {/* Additional Info */}
                                                                                {entry.data.additional_information && (
                                                                                    <div
                                                                                        style={{
                                                                                            marginTop: "12px",
                                                                                            background: "#eef4ff",
                                                                                            borderLeft: "4px solid #1b49ea",
                                                                                            borderRadius: "6px",
                                                                                            padding: "10px",
                                                                                        }}
                                                                                    >
                                                                                        <div
                                                                                            style={{
                                                                                                fontSize: "11px",
                                                                                                fontWeight: 700,
                                                                                                color: "#1b49ea",
                                                                                                marginBottom: "4px",
                                                                                                textTransform: "uppercase",
                                                                                            }}
                                                                                        >
                                                                                            Additional Information
                                                                                        </div>

                                                                                        <div
                                                                                            style={{
                                                                                                fontSize: "12px",
                                                                                                color: "#374151",
                                                                                                lineHeight: "1.5",
                                                                                            }}
                                                                                        >
                                                                                            {entry.data.additional_information}
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        ) : (
                                                                            // NEW: NO Debrief Flow - NO round card, reuses the
                                                                            // same card wrapper/header style as the YES card above
                                                                            <div
                                                                                key={index}
                                                                                style={{
                                                                                    marginBottom: "14px",
                                                                                    background: "#fef2f2",
                                                                                    border: "1px solid #fca5a5",
                                                                                    borderRadius: "10px",
                                                                                    padding: "12px",
                                                                                    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                                                                                }}
                                                                            >
                                                                                {/* Round Header */}
                                                                                <div
                                                                                    style={{
                                                                                        display: "flex",
                                                                                        justifyContent: "space-between",
                                                                                        alignItems: "center",
                                                                                        marginBottom: "10px",
                                                                                        borderBottom: "1px solid #fca5a5",
                                                                                        paddingBottom: "8px",
                                                                                    }}
                                                                                >
                                                                                    <div
                                                                                        style={{
                                                                                            fontWeight: 700,
                                                                                            fontSize: "13px",
                                                                                            color: "#dc2626",
                                                                                        }}
                                                                                    >
                                                                                        {entry.data.round_name}
                                                                                    </div>

                                                                                    <div
                                                                                        style={{
                                                                                            fontSize: "11px",
                                                                                            color: "#6b7280",
                                                                                        }}
                                                                                    >
                                                                                        {entry.data.submitted_at
                                                                                            ? new Date(
                                                                                                entry.data.submitted_at
                                                                                            ).toLocaleString()
                                                                                            : "No Date"}
                                                                                    </div>
                                                                                </div>

                                                                                <div
                                                                                    style={{
                                                                                        fontSize: "11px",
                                                                                        fontWeight: 700,
                                                                                        color: "#dc2626",
                                                                                        marginBottom: "6px",
                                                                                        textTransform: "uppercase",
                                                                                        letterSpacing: "0.5px",
                                                                                    }}
                                                                                >
                                                                                    NO
                                                                                </div>

                                                                                <div
                                                                                    style={{
                                                                                        padding: "8px 10px",
                                                                                        background: "#fff",
                                                                                        border: "1px solid #fca5a5",
                                                                                        borderRadius: "6px",
                                                                                        fontSize: "12px",
                                                                                        color: "#111827",
                                                                                        wordBreak: "break-word",
                                                                                        whiteSpace: "normal",
                                                                                        lineHeight: "1.5",
                                                                                    }}
                                                                                >
                                                                                    <strong>Reason:</strong>{" "}
                                                                                    {entry.data.reason || "No reason provided"}
                                                                                </div>
                                                                            </div>
                                                                        ),
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ),
                                                        disabled: true,
                                                    },
                                                ],
                                            }}
                                            trigger={["click"]}
                                            placement="bottomRight"
                                        >
                                            <Button
                                                type="text"
                                                size="small"
                                                onClick={(e) => e.preventDefault()}
                                                style={{
                                                    fontSize: "11px",
                                                    color: "#1b49ea",
                                                    padding: "2px 4px",
                                                    height: "24px",
                                                    minWidth: "auto",
                                                    fontWeight: 500,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    border: "1px solid #1b49ea",
                                                    borderRadius: "6px",
                                                    background: "#fff",
                                                }}
                                            >
                                                <span>View Debrief</span>
                                                <DownOutlined
                                                    style={{
                                                        fontSize: "9px",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        marginLeft: "4px",
                                                    }}
                                                />
                                            </Button>
                                        </Dropdown>
                                    ) : (
                                        "-"
                                    )}
                                </>
                            );
                        })()}
                    </td>

                    {/* ACTION BUTTONS */}
                    <td>
                        <Styled.IconDiv>
                            <Tooltip
                                title={
                                    isDownloading
                                        ? "Downloading..."
                                        : "Download Assessment Report"
                                }
                            >
                                <Styled.DownloadAssessmentButton
                                    onClick={() => downloadAssessmentReport(item)}
                                    disabled={!item.assessment_id || isDownloading}
                                    className={!item.assessment_id ? "disabled" : ""}
                                    $loading={isDownloading}
                                    style={{
                                        opacity: isDownloading ? 0.6 : undefined,
                                        cursor:
                                            !item.assessment_id || isDownloading
                                                ? "not-allowed"
                                                : "pointer",
                                    }}
                                >
                                    {isDownloading ? <LoadingOutlined spin /> : null}
                                </Styled.DownloadAssessmentButton>
                            </Tooltip>

                            <Dropdown
                                menu={{
                                    items: [
                                        item.job_desc && {
                                            key: "jd",
                                            label: "View JD",
                                            onClick: () =>
                                                downloadFile(
                                                    item.job_desc,
                                                    "JD",
                                                    item
                                                ),
                                        },

                                        item.misc_file_url && {
                                            key: "candidate-misc",
                                            label: "View Candidate Misc File",
                                            onClick: () =>
                                                downloadFile(
                                                    item.misc_file_url,
                                                    "Candidate_Misc_File",
                                                    item
                                                ),
                                        },

                                        item.miscellaneous_file && {
                                            key: "demand-misc",
                                            label: "View Demand Misc File",
                                            onClick: () =>
                                                downloadFile(
                                                    item.miscellaneous_file,
                                                    "Demand_Misc_File",
                                                    item
                                                ),
                                        },

                                        item.cv_url && {
                                            key: "resume",
                                            label: "View Resume",
                                            onClick: () =>
                                                downloadFile(
                                                    item.cv_url,
                                                    "Resume",
                                                    item
                                                ),
                                        },
                                    ].filter(Boolean),
                                }}
                                trigger={["click"]}
                                placement="bottomRight"
                            >
                                <Styled.OptionButton />
                            </Dropdown>
                        </Styled.IconDiv>
                    </td>
                </tr>
            );
        });

    /* ── Render pagination ───────────────────────────────────────────────────── */
    const renderPagination = () => (
        <div style={paginationStyles.container}>
            <div style={paginationStyles.leftSection}>
                <span>Show</span>
                <Styled.PaginationSelectWrapper>
                    <Select
                        value={itemsPerPage}
                        size="small"
                        dropdownMatchSelectWidth={false}
                        onChange={(value) => {
                            setItemsPerPage(value);
                            setCurrentPage(1);
                        }}
                    >
                        <Select.Option value={10}>10</Select.Option>
                        <Select.Option value={20}>20</Select.Option>
                        <Select.Option value={50}>50</Select.Option>
                    </Select>
                </Styled.PaginationSelectWrapper>
                <span>per page</span>
                <span style={{ marginLeft: "12px", color: "#9CA3AF" }}>|</span>
                <span style={{ marginLeft: "12px" }}>
                    {(currentPage - 1) * itemsPerPage + 1}–
                    {Math.min(currentPage * itemsPerPage, filteredDebriefList.length)} of{" "}
                    {filteredDebriefList.length} results
                </span>
            </div>

            <div style={paginationStyles.pageControls}>
                <button
                    style={paginationStyles.navBtn(currentPage === 1)}
                    onClick={() => setCurrentPage((p) => p - 1)}
                    disabled={currentPage === 1}
                >
                    ← Prev
                </button>

                {buildPageNumbers().map((page, idx) =>
                    page === "..." ? (
                        <span key={`ellipsis-${idx}`} style={paginationStyles.ellipsis}>
                            …
                        </span>
                    ) : (
                        <button
                            key={page}
                            style={paginationStyles.pageBtn(page === currentPage, false)}
                            onClick={() => setCurrentPage(page)}
                        >
                            {page}
                        </button>
                    )
                )}

                <button
                    style={paginationStyles.navBtn(currentPage === totalFilteredPages)}
                    onClick={() => setCurrentPage((p) => p + 1)}
                    disabled={currentPage === totalFilteredPages}
                >
                    Next →
                </button>
            </div>
        </div>
    );

    return (
        <Styled.MainDiv>
            <Sidemenu />

            <Styled.ContentWrapDiv>
                {/* ── Header ──────────────────────────────────────────────────────────── */}
                <Styled.TopContainer>
                    <div className="leftSide">Reference Profile</div>
                </Styled.TopContainer>

                {/* ── Filters ── */}
                <div
                    style={{
                        display: "flex",
                        gap: "12px",
                        marginBottom: "20px",
                        overflowX: "auto",
                        flexWrap: "nowrap",
                        paddingBottom: "6px",
                        scrollbarWidth: "thin",
                        scrollbarColor: "#D1D5DB transparent",
                    }}
                >
                    <Select
                        showSearch
                        placeholder="Select company"
                        onChange={(value) => handleFilterChange("company", value)}
                        value={filters.company || undefined}
                        allowClear
                        style={{ minWidth: "180px", flexShrink: 0 }}
                    >
                        {getCompanyOptions.map((company) => (
                            <Select.Option
                                key={company}
                                value={company}
                            >
                                {company}
                            </Select.Option>
                        ))}
                    </Select>

                    <Select
                        showSearch
                        placeholder="Select role"
                        onChange={(value) => handleFilterChange("role", value)}
                        value={filters.role || undefined}
                        allowClear
                        style={{ minWidth: "180px", flexShrink: 0 }}
                    >
                        {getRoleOptions.map((role) => (
                            <Select.Option key={role} value={role}>
                                <Tooltip title={role}>
                                    <span
                                        style={{
                                            display: "inline-block",
                                            width: "100%",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {role}
                                    </span>
                                </Tooltip>
                            </Select.Option>
                        ))}
                    </Select>

                    <Select
                        showSearch
                        placeholder="Select recruiter"
                        onChange={(value) => handleFilterChange("recruiter", value)}
                        value={filters.recruiter || undefined}
                        allowClear
                        style={{ minWidth: "180px", flexShrink: 0 }}
                    >
                        {getRecruiterOptions.map((recruiter) => (
                            <Select.Option
                                key={recruiter}
                                value={recruiter}
                            >
                                {recruiter}
                            </Select.Option>
                        ))}
                    </Select>

                    <Input
                        placeholder="Search Name / Email"
                        onChange={(e) => handleSearchChange(e.target.value)}
                        allowClear
                        style={{ maxWidth: "220px", flexShrink: 0 }}
                    />

                    <Select
                        showSearch
                        placeholder="Select Job Code"
                        onChange={(value) =>
                            handleFilterChange("jobCode", value)
                        }
                        value={filters.jobCode || undefined}
                        allowClear
                        style={{ minWidth: "180px", flexShrink: 0 }}
                    >
                        {getJobCodeOptions.map((jobCode) => (
                            <Select.Option
                                key={jobCode}
                                value={jobCode}
                            >
                                {jobCode}
                            </Select.Option>
                        ))}
                    </Select>
                </div>

                <Styled.TableWrap
                    style={{
                        overflowX: "auto",
                        overflowY: "auto",
                        maxHeight: "calc(100vh - 260px)",
                    }}
                >
                    {filteredDebriefList.length === 0 ? (
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                minHeight: "400px",
                                textAlign: "center",
                            }}
                        >
                            <img
                                src={require("../../images/Frame.png")}
                                alt="No Data"
                                style={{
                                    marginTop: "55px",
                                    width: "90%",
                                    maxWidth: "400px",
                                    height: "auto",
                                }}
                            />

                            <h2
                                style={{
                                    marginTop: "16px",
                                    color: "#6B7280",
                                    fontWeight: 500,
                                }}
                            >
                                No Result Found
                            </h2>
                        </div>
                    ) : (
                        <Styled.MainTable style={{ transition: "background-color 0.3s ease-in-out" }}>
                            <thead style={{ position: "sticky", top: 0, zIndex: 10, background: "#F2F6F8" }}>
                                <tr>
                                    <th className="sticky-th-1">Candidate Details</th>
                                    <th className="sticky-th-2">Account Name</th>
                                    <th className="sticky-th-3">Role Name</th>
                                    <th>Job Code</th>
                                    <th>Recruiter</th>
                                    <th>Total Exp</th>
                                    <th>Relevant Exp</th>
                                    <th>Candidate Status</th>
                                    <th>Client Feedback</th>
                                    <th>Client Remarks</th>
                                    <th>Fitment Score</th>
                                    <th>Assessment</th>
                                    <th>Tech Score</th>
                                    <th>Red Flag</th>
                                    <th>Debrief</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>{renderTableRows(itemsToDisplay)}</tbody>
                        </Styled.MainTable>
                    )}
                </Styled.TableWrap>

                {filteredDebriefList.length > 0 && renderPagination()}
            </Styled.ContentWrapDiv>
        </Styled.MainDiv>
    );
};

export default DebriefTable;